import { Button } from "@msuite/ui/button";
import { TextInput } from "@msuite/ui/form";
import { useSearchForm } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, type MenuItems } from "@msuite/ui/menu";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { Activity } from "react";
import { PLAN_CAPACITY_WEEK_OPTIONS, usePlanStore } from "@/components/plan/store";
import { CapacityManagerView } from "./manager-view";
import { CapacityPlannerView } from "./planner-view";
import { CapacityResources } from "./resources";

export function PlanCapacity() {
	/** Store */
	const showResources = usePlanStore((state) => state.showResources);
	const capacityView = usePlanStore((state) => state.capacityView);
	const setCapacityView = usePlanStore((state) => state.setCapacityView);
	const capacityWeeks = usePlanStore((state) => state.capacityWeeks);
	const setCapacityWeeks = usePlanStore((state) => state.setCapacityWeeks);

	/** Hooks */
	const { t } = useTranslation();
	const methods = useSearchForm();

	/** Functions */
	// How wide the window is, as a submenu rather than a second segmented control: it is a setting
	// somebody changes rarely, and six segments beside the two view ones would read as one control
	// with eight options.
	function getMenuItems(): MenuItems {
		return [
			{
				icon: icon("CalendarRange"),
				label: t("planning.number_of_weeks"),
				items: PLAN_CAPACITY_WEEK_OPTIONS.map((weeks) => ({
					icon: null,
					label: t("planning.calendar_weeks_count", { count: String(weeks) }),
					isActive: weeks === capacityWeeks,
					onClick: () => setCapacityWeeks(weeks),
				})),
			},
		];
	}

	/** Render */
	return (
		<div
			className="grow-0 gap-0 grid w-full min-w-0 h-full overflow-x-hidden overflow-y-hidden relative"
			style={{
				gridTemplateColumns: showResources ? "1fr max-content" : "1fr",
				gridTemplateRows: "minmax(0, 1fr)",
			}}
		>
			{/* The panel sits flush against the right edge, so the padding is the board column's and
			    not the grid's — the search row and the card below it share one right edge. */}
			<div className="w-full min-w-0 h-full min-h-0 v-stack pr-4 pb-4">
				{/* The wrapper carries flex: 1 to fill a toolbar row, so the field needs a row of its
				    own — dropped straight into the column it grows along the column's axis and takes
				    half the height off the board. */}
				<div className="h-stack shrink-0 p-1 justify-between">
					<div {...methods.getSearchInputWrapperProps()}>
						<TextInput {...methods.getSearchInputProps()} />
					</div>
					<div className="h-stack">
						<SegmentedControl
							value={capacityView}
							onChange={setCapacityView}
							items={[
								{
									label: t("planning.capacity_view_manager"),
									value: "manager",
									cyId: "capacity-view-manager",
								},
								{
									label: t("planning.capacity_view_planner"),
									value: "planner",
									cyId: "capacity-view-planner",
								},
							]}
						/>
						<Menu items={getMenuItems}>
							<Button
								size="sm"
								icon={<Icon name="Settings2" />}
								data-cy="capacity-board-settings"
							/>
						</Menu>
					</div>
				</div>
				{/* Both views stay mounted and the inactive one is hidden, the same bargain the two
				    boards make one level up: a hidden boundary keeps its DOM, its scroll position and
				    which rows were expanded, and runs no effects, so switching back costs a style
				    change rather than a refetch and a rebuild of the window. */}
				<Activity
					name="plan-capacity-manager"
					mode={capacityView === "manager" ? "visible" : "hidden"}
				>
					<CapacityManagerView searchValue={methods.searchValue} />
				</Activity>
				<Activity
					name="plan-capacity-planner"
					mode={capacityView === "planner" ? "visible" : "hidden"}
				>
					<CapacityPlannerView />
				</Activity>
			</div>
			{showResources && <CapacityResources />}
		</div>
	);
}
